/*

class to generate an appointment

https://www.hl7.org/fhir/appointment.html

{
  "state" : "<code>", // R!  busy | free | busy-unavailable | busy-tentative | entered-in-error
  "userType" : "<code>", // orgAdmin || providerAP || providerLP || patient
  "userId" : "<String>"
  "scheduleId" : "<String>"
  "slotId" : "<String>"
  "appointmentId" : "<String>"
  "cancelationReason" : { code }, // The coded reason for the appointment being cancelled
  "serviceCategory" : [{ code }], // A broad categorization of the service that is to be performed during this appointment
  "serviceType" : [{ code }], // The specific service that is to be performed during this appointment
  "specialty" : [{ code }], // The specialty of a practitioner that would be required to perform the service requested in this appointment
  "appointmentType" : { code }, // The style of appointment or patient that has been booked in the slot (not service type)
  "reasonCode" : [{ code }], // Coded reason this appointment is scheduled
  "reasonReference" : [{ Reference(Condition|Procedure|Observation|ImmunizationRecommendation) }], // Reason the appointment is to take place (resource)
  "priority" : "<unsignedInt>", // Used to make informed decisions if needing to re-prioritize
  "description" : "<string>", // Shown on a subject line in a meeting request, or appointment list
  "supportingInformation" : [{ Reference(Any) }], // Additional information to support the appointment
  "start" : "<instant>", // When appointment is to take place
  "end" : "<instant>", // When appointment is to conclude
  "minutesDuration" : "<positiveInt>", // Can be less than start/end (e.g. estimate)
  "slot" : [{ Reference(Slot) }], // The slots that this appointment is filling
  "created" : "<dateTime>", // The date that this appointment was initially created
  "comment" : "<string>", // Additional comments
  "patientInstruction" : "<string>", // Detailed information and instructions for the patient
  // "basedOn" : [{ Reference(ServiceRequest) }], // The service request this appointment is allocated to assess
  // "participant" : [{ // R!  Participants involved in appointment
  //   "type" : [{ code }], // Role of participant in the appointment
  //   "actor" : { Reference(Patient|Practitioner|PractitionerRole|RelatedPerson|Device|HealthcareService|Location) }, // Person, Location/HealthcareService or Device
  //   "required" : "<code>", // required | optional | information-only
  //   "status" : "<code>", // R!  accepted | declined | tentative | needs-action
  //   "period" : { Period } // Participation period of the actor
  // }],
  "requestedPeriod" : [{ Period }] // Potential date/time interval(s) requested to allocate the appointment within
}

*/

// Import libraries
const Joi = require('joi');

// Import variables
const { STATES, USER_TYPES, REASON_REFERENCES } = require('@bcm/constants');
const { joiValidateObj, identifiers } = require('../datastructComponents');
const { DataTableBase } = require('./base.class');
const { codeSchema } = require('./code.class');

// Create schema
const schema = {
  identifier: Joi.any().optional(),
  state: Joi.string().valid( ...Object.keys(STATES) ).required(),
  userType: Joi.string().valid( ...Object.keys( USER_TYPES) ).required(),
  userId: Joi.string().required(),
  scheduleId: Joi.string().required(),
  slotId: Joi.string().required(),
  appointmentId: Joi.string().required(),
  cancelationReason: codeSchema,
  serviceCategory: Joi.array().items(codeSchema).optional(),
  serviceType:  Joi.array().items(codeSchema).optional(),
  specialty: Joi.array().items(codeSchema).optional(),
  appointmentType: codeSchema,
  reasonCode: Joi.array().items(codeSchema).optional(),
  reasonReference: Joi.array().items( Joi.string().valid(...Object.keys(REASON_REFERENCES)) ).optional(),
  priority: Joi.string().optional(),
  description: Joi.string().optional(),
  supportingInformation: Joi.array().items( Joi.string().optional() ),
  start: Joi.string().required(),
  end: Joi.string().required(),
  minutesDuration: Joi.string().optional(),
  slot: Joi.array().items( Joi.string().optional() ),
  comment: Joi.string().optional(),
  patientInstruction: Joi.string().optional(),
  requestPeriodStart: Joi.string().optional(),
  requestPeriodEnd: Joi.string().optional(),
};

// Define class
class Appointment extends DataTableBase {
  constructor ( obj ) {
    joiValidateObj(schema, obj);
    super (
      identifiers.appointment.buildPk(obj.scheduleId),
      identifiers.appointment.buildSk(obj.state, obj.userType, obj.userId, obj.scheduleId, obj.slotId, obj.appointmentId)
    );
    for (const key of Object.keys(obj)) {
      if(obj[key] || obj[key] === false) {
        this[key] = obj[key];
      }
    }
  }
}

exports.Appointment = Appointment;
exports.appointmentSchema = schema;